<template>
    <form @submit.prevent="handleSubmit" class="space-y-6">
        <div
            v-if="formError"
            class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4"
        >
            <div class="flex">
                <div class="flex-shrink-0">
                    <div class="h-5 w-5 text-red-400">⚠️</div>
                </div>
                <div class="ml-3">
                    <h3
                        class="text-sm font-medium text-red-800 dark:text-red-200"
                    >
                        Error
                    </h3>
                    <div class="mt-2 text-sm text-red-700 dark:text-red-300">
                        <ul
                            v-if="formErrors.length > 0"
                            class="list-disc list-inside space-y-1"
                        >
                            <li
                                v-for="(error, index) in formErrors"
                                :key="index"
                            >
                                {{ error }}
                            </li>
                        </ul>
                        <p v-else>{{ formError }}</p>
                    </div>
                </div>
            </div>
        </div>

        <!-- Image Upload Area -->
        <div>
            <label
                class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2"
            >
                Question Image (Optional)
            </label>
            <div
                class="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4 text-center hover:border-gray-400 dark:hover:border-gray-500 transition-colors cursor-pointer"
                @click="triggerFileInput"
                @dragover.prevent="dragOver = true"
                @dragleave.prevent="dragOver = false"
                @drop.prevent="handleImageDrop"
                :class="{
                    'border-blue-500 bg-blue-50 dark:bg-blue-900/20': dragOver,
                }"
            >
                <input
                    ref="imageInput"
                    type="file"
                    class="hidden"
                    accept="image/*"
                    @change="handleImageSelect"
                />

                <div
                    v-if="!imagePreview && (!existingImage || imageRemoved)"
                    class="space-y-2 py-4"
                >
                    <ImageIcon class="mx-auto h-10 w-10 text-gray-400" />
                    <p class="text-sm text-gray-600 dark:text-gray-400">
                        Drop image here or click to browse
                    </p>
                    <p class="text-xs text-gray-500 dark:text-gray-500">
                        Supported: JPG, PNG, WebP (Max 2MB)
                    </p>
                </div>

                <div v-else class="space-y-2 py-2">
                    <img
                        v-if="imagePreview"
                        :src="imagePreview"
                        alt="Preview"
                        class="mx-auto max-h-48 rounded-lg object-contain"
                    />
                    <img
                        v-else-if="existingImage && !imageRemoved"
                        :src="existingImage"
                        alt="Existing image"
                        class="mx-auto max-h-48 rounded-lg object-contain"
                    />
                    <p
                        class="text-sm font-medium text-gray-900 dark:text-white"
                    >
                        {{ imageName }}
                    </p>
                    <div class="flex justify-center gap-2">
                        <button
                            type="button"
                            @click.stop="removeImage"
                            class="text-xs text-red-600 hover:text-red-500"
                        >
                            Remove image
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <div>
            <label
                class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2"
            >
                Question Text
            </label>
            <textarea
                v-model="form.question"
                rows="4"
                class="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm dark:bg-gray-700 dark:text-white"
                placeholder="Enter your question here..."
                :class="{
                    'border-red-500 focus:border-red-500 focus:ring-red-500':
                        fieldErrors.question,
                }"
                required
            ></textarea>
            <p
                v-if="fieldErrors.question"
                class="mt-1 text-sm text-red-600 dark:text-red-400"
            >
                {{ fieldErrors.question }}
            </p>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
                <label
                    class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2"
                >
                    Question Type
                </label>
                <select
                    v-model="form.type"
                    class="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm py-2.5 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm dark:bg-gray-700 dark:text-white"
                    :class="{
                        'border-red-500 focus:border-red-500 focus:ring-red-500':
                            fieldErrors.type,
                    }"
                    required
                >
                    <option value="multiple_choice">Multiple Choice</option>
                    <option value="multiple_select">Multiple Select</option>
                    <option value="true_false">True/False</option>
                    <option value="essay">Essay</option>
                </select>
                <p
                    v-if="fieldErrors.type"
                    class="mt-1 text-sm text-red-600 dark:text-red-400"
                >
                    {{ fieldErrors.type }}
                </p>
            </div>

            <div>
                <label
                    class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2"
                >
                    Points
                </label>
                <input
                    type="number"
                    v-model="form.points"
                    step="0.01"
                    min="0"
                    class="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm py-2.5 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm dark:bg-gray-700 dark:text-white"
                    :class="{
                        'border-red-500 focus:border-red-500 focus:ring-red-500':
                            fieldErrors.points,
                    }"
                    placeholder="1.00"
                    required
                />
                <p
                    v-if="fieldErrors.points"
                    class="mt-1 text-sm text-red-600 dark:text-red-400"
                >
                    {{ fieldErrors.points }}
                </p>
            </div>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
                <label
                    class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2"
                >
                    Difficulty Level
                </label>
                <select
                    v-model="form.difficulty"
                    class="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm py-2.5 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm dark:bg-gray-700 dark:text-white"
                    :class="{
                        'border-red-500 focus:border-red-500 focus:ring-red-500':
                            fieldErrors.difficulty,
                    }"
                    required
                >
                    <option value="easy">Easy</option>
                    <option value="medium">Medium</option>
                    <option value="hard">Hard</option>
                </select>
                <p
                    v-if="fieldErrors.difficulty"
                    class="mt-1 text-sm text-red-600 dark:text-red-400"
                >
                    {{ fieldErrors.difficulty }}
                </p>
            </div>

            <div>
                <SearchableSelect
                    v-model="form.subject_id"
                    :options="subjects"
                    label="Subject"
                    placeholder="Select Subject"
                    optionLabel="name"
                    optionValue="id"
                    :required="true"
                    :error="fieldErrors.subject_id"
                />
            </div>
        </div>

        <div v-if="!isCurrentUserTeacher">
            <SearchableSelect
                v-model="form.teacher_id"
                :options="teachers"
                label="Assigned Teacher"
                placeholder="Select Teacher"
                :optionLabel="(teacher) => teacher.user?.name || ''"
                optionValue="id"
                :required="true"
                :error="fieldErrors.teacher_id"
            />
        </div>

        <div class="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
            <h4
                class="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3"
            >
                Settings
            </h4>

            <div class="flex items-center mb-3">
                <input
                    type="checkbox"
                    v-model="form.is_active"
                    class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded dark:bg-gray-700"
                />
                <label class="ml-3 block text-sm text-gray-900 dark:text-white">
                    Question is active
                </label>
            </div>
        </div>

        <div>
            <label
                class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2"
            >
                Explanation (Optional)
            </label>
            <textarea
                v-model="form.explanation"
                rows="3"
                class="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm py-2.5 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm dark:bg-gray-700 dark:text-white"
                placeholder="Provide an explanation for the correct answer..."
            ></textarea>
        </div>

        <div>
            <label
                class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2"
            >
                Tags
            </label>
            <input
                type="text"
                v-model="tagsInput"
                placeholder="e.g., math, algebra, equations"
                class="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm py-2.5 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm dark:bg-gray-700 dark:text-white"
                :class="{
                    'border-red-500 focus:border-red-500 focus:ring-red-500':
                        fieldErrors.tags,
                }"
            />
            <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Separate multiple tags with commas
            </p>
            <p
                v-if="fieldErrors.tags"
                class="mt-1 text-sm text-red-600 dark:text-red-400"
            >
                {{ fieldErrors.tags }}
            </p>
        </div>

        <div
            v-if="form.type !== 'essay'"
            class="pt-6 border-t border-gray-200 dark:border-gray-700"
        >
            <div class="mb-4">
                <h4 class="text-lg font-semibold text-gray-900 dark:text-white">
                    Answer Options
                </h4>
                <p class="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    <span v-if="form.type === 'multiple_choice'">
                        Select exactly one correct answer
                    </span>
                    <span v-else-if="form.type === 'multiple_select'">
                        Select one or more correct answers
                    </span>
                    <span v-else-if="form.type === 'true_false'">
                        Select the correct answer (True or False)
                    </span>
                </p>
                <p
                    v-if="fieldErrors.options"
                    class="mt-1 text-sm text-red-600 dark:text-red-400"
                >
                    {{ fieldErrors.options }}
                </p>
            </div>

            <div class="space-y-3">
                <div
                    v-for="(option, index) in form.options"
                    :key="index"
                    class="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
                >
                    <span
                        class="text-sm font-bold text-gray-600 dark:text-gray-400 w-8 text-center"
                    >
                        {{ String.fromCharCode(65 + index) }}.
                    </span>
                    <input
                        type="text"
                        v-model="option.option_text"
                        :placeholder="
                            form.type === 'true_false'
                                ? index === 0
                                    ? 'True'
                                    : 'False'
                                : 'Option text'
                        "
                        class="flex-1 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm dark:bg-gray-700 dark:text-white"
                        :required="form.type !== 'essay'"
                        :readonly="form.type === 'true_false'"
                    />
                    <div class="flex items-center space-x-2">
                        <input
                            type="radio"
                            v-if="form.type === 'multiple_choice'"
                            v-model="correctOptionIndex"
                            :value="index"
                            class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                        />
                        <input
                            type="checkbox"
                            v-else-if="form.type === 'multiple_select'"
                            v-model="option.is_correct"
                            class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <input
                            type="radio"
                            v-else-if="form.type === 'true_false'"
                            v-model="correctOptionIndex"
                            :value="index"
                            class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                        />
                        <span
                            class="text-xs font-medium text-gray-700 dark:text-gray-300"
                            >Correct</span
                        >
                    </div>
                    <button
                        type="button"
                        @click="removeOption(index)"
                        v-if="
                            form.type !== 'true_false' &&
                            form.options.length > 2
                        "
                        class="text-red-600 hover:text-red-800 text-sm font-medium"
                    >
                        Remove
                    </button>
                </div>
            </div>

            <button
                type="button"
                @click="addOption"
                v-if="form.type !== 'true_false'"
                class="mt-3 inline-flex items-center px-4 py-2 border border-blue-300 dark:border-blue-700 shadow-sm text-sm font-medium rounded-md text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900 hover:bg-blue-100 dark:hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
                <Plus class="w-4 h-4 mr-2" />
                Add Option
            </button>
        </div>

        <div v-else class="pt-6 border-t border-gray-200 dark:border-gray-700">
            <div
                class="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4"
            >
                <div class="flex">
                    <div class="flex-shrink-0">
                        <Info class="h-5 w-5 text-blue-400" />
                    </div>
                    <div class="ml-3">
                        <h3
                            class="text-sm font-medium text-blue-800 dark:text-blue-200"
                        >
                            Essay Question
                        </h3>
                        <div
                            class="mt-2 text-sm text-blue-700 dark:text-blue-300"
                        >
                            This is an essay question type. Students will
                            provide a written response instead of selecting from
                            predefined options.
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div
            class="flex justify-end space-x-4 pt-6 border-t border-gray-200 dark:border-gray-700"
        >
            <button
                type="button"
                @click="$emit('cancel')"
                class="px-4 py-2 text-sm font-medium text-gray-700 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600"
            >
                Cancel
            </button>
            <button
                type="submit"
                :disabled="loading"
                class="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
                <Loader2
                    v-if="loading"
                    class="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline"
                />
                {{
                    loading
                        ? "Saving..."
                        : isEditing
                          ? "Update Question"
                          : "Create Question"
                }}
            </button>
        </div>
    </form>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, toRaw } from "vue";
import { useQuestionBankStore } from "@/stores/questionBankStore";
import { useAuthStore } from "@/stores/authStore";
import { subjectApi, type Subject } from "@/api/subject";
import { teacherApi, type Teacher } from "@/api/teacher";
import SearchableSelect from "@/components/SearchableSelect.vue";
import { Plus, Info, Loader2, ImageIcon } from "@lucide/vue";

interface QuestionOption {
    option_text: string;
    is_correct: boolean;
}

interface QuestionBankForm {
    question: string;
    type: string;
    points: number;
    difficulty: string;
    subject_id: string;
    teacher_id?: string;
    is_active: boolean;
    explanation: string;
    tags: string[];
    image?: File;
    remove_image?: boolean;
    options: QuestionOption[];
}

const props = defineProps<{
    question?: any;
    isEditing: boolean;
}>();

const emit = defineEmits<{
    saved: [];
    cancel: [];
}>();

const questionBankStore = useQuestionBankStore();
const authStore = useAuthStore();
const loading = ref(false);
const subjects = ref<Subject[]>([]);
const teachers = ref<Teacher[]>([]);
const tagsInput = ref("");

const formError = ref("");
const formErrors = ref<string[]>([]);
const fieldErrors = ref<Record<string, string>>({});

const imageInput = ref<HTMLInputElement | null>(null);
const imageFile = ref<File | null>(null);
const imagePreview = ref<string | null>(null);
const dragOver = ref(false);
const imageRemoved = ref(false);

const existingImage = computed(() => props.question?.image_url);

const imageName = computed(() => {
    if (imageFile.value) return imageFile.value.name;
    if (existingImage.value) return "Current image";
    return "";
});

const form = ref<QuestionBankForm>({
    question: "",
    type: "multiple_choice",
    points: 1,
    difficulty: "medium",
    subject_id: "",
    teacher_id: "",
    is_active: true,
    explanation: "",
    tags: [],
    options: [
        { option_text: "", is_correct: true },
        { option_text: "", is_correct: false },
    ],
});

const isCurrentUserTeacher = computed(() => {
    return authStore.hasRole("teacher");
});

const resetForm = () => {
    form.value = {
        question: "",
        type: "multiple_choice",
        points: 1,
        difficulty: "medium",
        subject_id: "",
        teacher_id: "",
        is_active: true,
        explanation: "",
        tags: [],
        options: [
            { option_text: "", is_correct: true },
            { option_text: "", is_correct: false },
        ],
    };
    tagsInput.value = "";
    imageFile.value = null;
    imagePreview.value = null;
    dragOver.value = false;
    imageRemoved.value = false;
};

watch(
    () => props.question,
    (newQuestion: any) => {
        if (newQuestion && props.isEditing) {
            form.value = {
                question: newQuestion.question || "",
                type: newQuestion.type || "multiple_choice",
                points: newQuestion.points || 1,
                difficulty: newQuestion.difficulty || "medium",
                subject_id: newQuestion.subject_id || "",
                teacher_id: newQuestion.teacher_id || "",
                is_active: newQuestion.is_active ?? true,
                explanation: newQuestion.explanation || "",
                tags: newQuestion.tags || [],
                options: newQuestion.options || [
                    { option_text: "", is_correct: false },
                    { option_text: "", is_correct: false },
                ],
            };
            tagsInput.value = (newQuestion.tags || []).join(", ");
            imageRemoved.value = false;
        } else {
            resetForm();
        }
    },
    { immediate: true },
);

const triggerFileInput = () => {
    imageInput.value?.click();
};

const handleImageSelect = (event: Event) => {
    const target = event.target as HTMLInputElement;
    const file = target.files?.[0];
    if (file) {
        processImageFile(file);
    }
};

const handleImageDrop = (event: DragEvent) => {
    dragOver.value = false;
    const file = event.dataTransfer?.files?.[0];
    if (file && file.type.startsWith("image/")) {
        processImageFile(file);
    }
};

const processImageFile = (file: File) => {
    if (!file.type.startsWith("image/")) {
        alert("Please select an image file");
        return;
    }
    if (file.size > 2 * 1024 * 1024) {
        alert("Image size must be less than 2MB");
        return;
    }

    imageFile.value = file;
    form.value.image = file;
    imageRemoved.value = false;

    const reader = new FileReader();
    reader.onload = (e) => {
        imagePreview.value = e.target?.result as string;
    };
    reader.readAsDataURL(file);
};

const removeImage = () => {
    imageFile.value = null;
    imagePreview.value = null;
    imageRemoved.value = true;
    form.value.image = undefined;

    if (props.isEditing && props.question?.image_path) {
        form.value.remove_image = true;
    } else {
        form.value.remove_image = undefined;
    }

    if (imageInput.value) {
        imageInput.value.value = "";
    }
};

watch(tagsInput, (newValue: string) => {
    form.value.tags = newValue
        .split(",")
        .map((tag: string) => tag.trim())
        .filter((tag: string) => tag.length > 0);
});

watch(
    () => form.value.type,
    (newType: string) => {
        if (newType === "true_false") {
            form.value.options = [
                { option_text: "True", is_correct: false },
                { option_text: "False", is_correct: false },
            ];
        } else if (newType === "essay") {
            form.value.options = [];
        } else if (form.value.options.length < 2) {
            form.value.options = [
                { option_text: "", is_correct: true },
                { option_text: "", is_correct: false },
            ];
        }
    },
);

const correctOptionIndex = computed({
    get: () => {
        const index = form.value.options.findIndex((opt) => opt.is_correct);
        return index >= 0 ? index : 0;
    },
    set: (value: number) => {
        form.value.options.forEach((opt, index) => {
            opt.is_correct = index === value;
        });
    },
});

const addOption = () => {
    form.value.options.push({ option_text: "", is_correct: false });
    if (form.value.type === "multiple_choice") {
        const hasCorrect = form.value.options.some((opt) => opt.is_correct);
        if (!hasCorrect && form.value.options.length > 0) {
            form.value.options[0].is_correct = true;
        }
    }
};

const removeOption = (index: number) => {
    form.value.options.splice(index, 1);
    if (form.value.type === "multiple_choice") {
        const hasCorrect = form.value.options.some((opt) => opt.is_correct);
        if (!hasCorrect && form.value.options.length > 0) {
            form.value.options[0].is_correct = true;
        }
    }
};

const fetchSubjects = async () => {
    try {
        const response = await subjectApi.getAll({ per_page: 1000 });
        subjects.value = response.data?.items || [];
    } catch (error) {
        console.error("Failed to fetch subjects:", error);
    }
};

const fetchTeachers = async () => {
    try {
        const response = await teacherApi.getAll({ per_page: 1000 });
        teachers.value = response.data?.items || [];
    } catch (error) {
        console.error("Failed to fetch teachers:", error);
    }
};

const setTeacherIdForCurrentUser = () => {
    if (isCurrentUserTeacher.value && authStore.user) {
        const currentTeacher = teachers.value.find(
            (teacher: any) => teacher.user?.id === authStore.user?.id,
        );
        if (currentTeacher) {
            form.value.teacher_id = currentTeacher.id;
        }
    }
};

const handleSubmit = async () => {
    loading.value = true;
    formError.value = "";
    formErrors.value = [];
    fieldErrors.value = {};

    try {
        const submitData: any = { ...form.value };

        if (submitData.type !== "essay") {
            submitData.options = submitData.options.filter(
                (opt: QuestionOption) => opt.option_text.trim() !== "",
            );
        }

        if (isCurrentUserTeacher.value) {
            setTeacherIdForCurrentUser();
        }

        if (props.isEditing && props.question) {
            await questionBankStore.updateQuestion(
                props.question.id,
                submitData,
            );
        } else {
            await questionBankStore.createQuestion(submitData);
        }
        emit("saved");
    } catch (error: any) {
        console.error("Failed to upload, or unexpected error:", error);

        if (error.response?.data) {
            const data = error.response.data;
            formError.value = data.message || "Failed to save question";

            if (data.errors) {
                const errorMessages: string[] = [];
                const fieldErrorMap: Record<string, string> = {};

                for (const [field, messages] of Object.entries(data.errors)) {
                    const messageList = Array.isArray(messages)
                        ? messages
                        : [messages];
                    errorMessages.push(...messageList);
                    fieldErrorMap[field] = messageList[0];
                }

                formErrors.value = errorMessages;
                fieldErrors.value = fieldErrorMap;
            }
        } else {
            formError.value = error.message || "Failed to save question";
            formErrors.value = [formError.value];
        }
    } finally {
        loading.value = false;
    }
};

onMounted(async () => {
    await Promise.all([fetchSubjects(), fetchTeachers()]);

    if (isCurrentUserTeacher.value) {
        setTeacherIdForCurrentUser();
    }
});
</script>
